import React from "react";
import { CourseResource } from "../../../stores/CoursesStore/Types";
import { SkillArea } from "./Types";

interface Props {
  courses: CourseResource[];
  userIsInUk?: boolean;
  skillArea: SkillArea;
  totalCoursesCount: number;
}

const courseImage = (course: CourseResource, userIsInUk?: boolean) =>
  (userIsInUk && course.preview_headshot_alt_image_url) ||
  course.preview_headshot_image_url ||
  course.brand_logo_url ||
  course.brand_alt_logo_url;

const courseDescription = (course: CourseResource) =>
  course.preview_mini_description || course.tile_description || "";

const heroTitle = (skillArea: SkillArea) =>
  skillArea.name_html || `${skillArea.name} courses`;

const heroDescription = (skillArea: SkillArea) =>
  skillArea.description_html || skillArea.description || "";

const Hero = ({ skillArea, courses, totalCoursesCount, userIsInUk }: Props) => {
  return (
    <header className="hero">
      <div className="hero-pattern" aria-hidden="true">
        <svg
          width="100%"
          height="100%"
          preserveAspectRatio="xMidYMid slice"
          xmlns="http://www.w3.org/2000/svg"
        >
          <svg x="4%" y="70%" overflow="visible">
            <use
              href="#blob-1"
              fill="#FFA5BC"
              transform="scale(1.26)"
              opacity="0.16"
            />
          </svg>

          <svg x="88%" y="20%" overflow="visible">
            <use
              href="#blob-1"
              fill="#B798F2"
              transform="scale(1.54)"
              opacity="0.14"
            />
          </svg>

          <svg x="46%" y="-18%" overflow="visible">
            <use
              href="#blob-2"
              fill="#E8EBFF"
              transform="scale(1.12)"
              opacity="0.5"
            />
          </svg>

          <svg x="70%" y="88%" overflow="visible">
            <use
              href="#blob-2"
              fill="#F3EC7A"
              transform="scale(0.7)"
              opacity="0.3"
            />
          </svg>
        </svg>
      </div>

      <div className="container">
        <nav className="breadcrumbs" aria-label="Breadcrumb">
          <a href="/">Home</a>
          <span className="crumb-sep">/</span>
          <a href="/courses">Courses</a>
          <span className="crumb-sep">/</span>
          <span aria-current="page">{skillArea?.name}</span>
        </nav>

        <div className="hero-grid">
          <div>
            <span className="eyebrow">Skill area</span>
            <h1
              className="hero-title"
              dangerouslySetInnerHTML={{ __html: heroTitle(skillArea) }}
            ></h1>
            <p
              className="hero-sub"
              dangerouslySetInnerHTML={{
                __html: heroDescription(skillArea),
              }}
            ></p>

            <div className="btn-row">
              <a
                href="#"
                data-smooth-scroll-to="#courses"
                className="btn btn-primary"
              >
                Explore the courses &darr;
              </a>

              <a
                href={`/courses/${skillArea?.sample_course_slug}`}
                className="btn btn-secondary"
              >
                Try a free sample
              </a>
            </div>

            <div className="stats" aria-label="Skill area at a glance">
              <div>
                <strong>{totalCoursesCount}</strong> Courses
              </div>

              <div>
                <strong>4.8★</strong> Average rating
              </div>

              <div>
                <strong>CPD</strong> Accredited
              </div>
            </div>
          </div>

          <div className="hero-visual" aria-label="Featured courses">
            {courses.map((course) => {
              const imageUrl = courseImage(course, userIsInUk);

              return (
                <a
                  className="hero-card"
                  href={course.course_path}
                  key={course.id}
                >
                  <div className="card-thumb">
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={course.name}
                        loading="eager"
                        width="230"
                        height="175"
                        decoding="async"
                      />
                    ) : (
                      <span className="cover-placeholder">[ Image ]</span>
                    )}
                  </div>

                  <div className="card-body">
                    <p className="card-title">{course.name}</p>
                    <p>{courseDescription(course)}</p>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Hero;
